import { apiClient, type PageData } from './client'
import type {
  Order,
  OrderListParams,
  OrderDetailResponse,
  DeliveryParams,
  SplitDeliveryParams,
  Refund,
  RefundListParams,
  RefundProcessParams,
  RefundReason,
  OrderStatus,
} from '@/schemas/supplier'

// ==================== 订单 API ====================

export const supplierOrderApi = {
  /**
   * 获取订单列表
   */
  list: async (params: OrderListParams): Promise<PageData<Order>> => {
    const searchParams = new URLSearchParams()

    searchParams.set('page', String(params.page || 1))
    searchParams.set('page_size', String(params.page_size || 30))

    if (params.status) searchParams.set('status', params.status)
    if (params.real_name) searchParams.set('real_name', params.real_name)
    if (params.data) searchParams.set('data', params.data)
    if (params.pay_type) searchParams.set('pay_type', params.pay_type)
    if (params.type) searchParams.set('type', params.type)

    const response = await apiClient.get<PageData<Order>>(
      `/supplier/orders?${searchParams.toString()}`
    )
    return response.data
  },

  /**
   * 获取订单详情
   */
  detail: async (id: number): Promise<Order> => {
    const response = await apiClient.get<OrderDetailResponse>(
      `/supplier/orders/${id}`
    )
    // 合并 cart_infos 到 order.cart_info
    const data = response.data
    const order = data.order
    if (data.cart_infos && data.cart_infos.length > 0) {
      order.cart_info = data.cart_infos
    }
    return order
  },

  /**
   * 订单发货
   */
  delivery: async (id: number, data: DeliveryParams): Promise<void> => {
    await apiClient.put<void>(
      `/supplier/orders/${id}/delivery`,
      data
    )
  },

  /**
   * 拆单发货
   */
  splitDelivery: async (id: number, data: SplitDeliveryParams): Promise<void> => {
    await apiClient.put<void>(
      `/supplier/orders/${id}/split_delivery`,
      data
    )
  },

  /**
   * 获取订单状态记录（订单记录）
   */
  statusList: async (id: number): Promise<OrderStatus[]> => {
    const response = await apiClient.get<OrderStatus[]>(
      `/supplier/orders/${id}/status`
    )
    return response.data
  },
}

// ==================== 退款 API ====================

export const supplierRefundApi = {
  /**
   * 获取退款列表
   */
  list: async (params: RefundListParams): Promise<PageData<Refund>> => {
    const searchParams = new URLSearchParams()

    searchParams.set('page', String(params.page || 1))
    searchParams.set('page_size', String(params.page_size || 30))

    if (params.order_id) searchParams.set('order_id', params.order_id)
    if (params.time) searchParams.set('time', params.time)
    if (params.apply_type !== undefined) {
      searchParams.set('apply_type', String(params.apply_type))
    }
    if (params.refund_type !== undefined) {
      searchParams.set('refund_type', String(params.refund_type))
    }
    if (params.refund_reason) {
      searchParams.set('refund_reason', params.refund_reason)
    }

    const response = await apiClient.get<PageData<Refund>>(
      `/supplier/refunds?${searchParams.toString()}`
    )
    return response.data
  },

  /**
   * 获取退款详情
   */
  detail: async (id: number): Promise<Refund> => {
    const response = await apiClient.get<Refund>(
      `/supplier/refunds/${id}`
    )
    return response.data
  },

  /**
   * 处理退款（同意/拒绝）
   */
  process: async (id: number, data: RefundProcessParams): Promise<void> => {
    await apiClient.put<void>(
      `/supplier/refunds/${id}`,
      data
    )
  },

  /**
   * 获取退款原因列表
   */
  reasons: async (): Promise<RefundReason[]> => {
    const response = await apiClient.get<RefundReason[]>(
      '/supplier/refunds/reason'
    )
    return response.data
  },

  /**
   * 确认收货并退款（退货退款流程）
   */
  confirmReceive: async (id: number): Promise<void> => {
    await apiClient.put<void>(
      `/supplier/refunds/${id}/confirm`
    )
  },
}

// ==================== 保证金 API ====================

// 保证金余额响应
interface DepositBalanceResponse {
  balance: number
}

// 保证金变更记录
export interface DepositLog {
  id: number
  created_at: string
  type: string
  amount: number
  balance_before: number
  balance_after: number
  order_id: number | null
  order_refund_id: number | null
  remark: string
  operator_id: number | null
}

// 保证金变更类型中文映射
export const DEPOSIT_TYPE_LABELS: Record<string, string> = {
  order_deduct: '订单扣除',
  order_refund: '订单退款',
  admin_deposit: '管理员充值',
  admin_deduct: '管理员扣减',
}

// 保证金记录列表响应
interface DepositLogListResponse {
  list: DepositLog[]
  total: number
}

export const supplierDepositApi = {
  /**
   * 获取保证金余额
   */
  getBalance: async (): Promise<number> => {
    const response = await apiClient.get<DepositBalanceResponse>('/supplier/deposit')
    return response.data.balance
  },

  /**
   * 获取保证金变更记录
   */
  getLogs: async (params: { page?: number; page_size?: number } = {}): Promise<DepositLogListResponse> => {
    const searchParams = new URLSearchParams()
    searchParams.set('page', String(params.page || 1))
    searchParams.set('page_size', String(params.page_size || 30))

    const response = await apiClient.get<DepositLogListResponse>(
      `/supplier/deposit/logs?${searchParams.toString()}`
    )
    return response.data
  },
}
